import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useSuspenseQuery } from "@apollo/client/react";
import { extractI18nLabel } from "~/common/functions";
import {
  GetAreaGeoItemDetailDocument,
  type GetAreaGeoItemDetailQuery,
  type GetAreaGeoItemDetailQueryVariables,
} from "~/features/recordGeoItems/gqls";
import { useTenant } from "~/features/tenant/context";
import type { Locale } from "~/i18n/resolveLocale";

type AreaGeoItemEntry = Extract<
  GetAreaGeoItemDetailQuery["cnutGetRecordGeoItemDetail"],
  { __typename: "RecordGeoItemDetailOutput" }
>["entries"][number];

/** A record may link the same GeoItem several times, each link with its own description. */
export const toAreaGeoItemEntryRows = (entries: readonly AreaGeoItemEntry[], locale: Locale) =>
  entries.map((entry) => ({
    label: extractI18nLabel(entry.i18nLabel, locale),
    date: entry.date,
    endDate: entry.endDate,
    memo: (entry.metadata as { memo?: string }).memo ?? null,
  }));

/** Full detail of a single record geo-item (read-only area view). */
export function useAreaGeoItemDetail(recordId: string, geoItemId: string) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  // cache-and-network so the detail revalidates on mount — after an edit the mutation returns
  // only ids (can't normalize the change), so a plain cache-first read would show stale data.
  const { data, error } = useSuspenseQuery<GetAreaGeoItemDetailQuery, GetAreaGeoItemDetailQueryVariables>(
    GetAreaGeoItemDetailDocument,
    {
      variables: { tenantId, recordId, geoItemId },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
  );

  if (error) {
    const isNotFound =
      CombinedGraphQLErrors.is(error) && error.errors.some(({ extensions }) => extensions?.status === 404);
    if (isNotFound) return null;
    throw error;
  }

  const response = data?.cnutGetRecordGeoItemDetail;
  return response?.__typename === "RecordGeoItemDetailOutput" ? response : null;
}
